import { useMemo, useState } from 'react';
import { useBrainStore } from '@/state/brainStore';
import type { StudioId } from '@/domain/types';
import type { ReportingScope } from '@/domain/reportingCompare';
import { singleStudioScope } from '@/domain/reportingCompare';
import InfoTip from '@/components/InfoTip';
import { rebookAttribution, agentPerformance, REBOOK_MANAGER_TARGET } from '@/domain/reportingEngine';

type View = 'artists' | 'agents' | 'managers';

function pct(n: number | null): string {
  return n === null ? '—' : `${Math.round(n * 100)}%`;
}

const VIEWS: Array<{ id: View; label: string }> = [
  { id: 'artists', label: 'Artists' },
  { id: 'agents', label: 'Agents' },
  { id: 'managers', label: 'Managers' },
];

/** Rankings by counts/rates - no dollar figures on this tab. Three ranked
 *  views: artists by in-chair rebooks facilitated, agents by conversion
 *  (calls → deposits), and studio managers by rebook rate vs the 40% target. */
export default function LeaderboardTab({ scope }: { scope: ReportingScope }) {
  const bookings = useBrainStore((s) => s.bookings);
  const artists = useBrainStore((s) => s.artists);
  const agents = useBrainStore((s) => s.agents);
  const studios = useBrainStore((s) => s.studios);
  const deposits = useBrainStore((s) => s.deposits);
  const customers = useBrainStore((s) => s.customers);
  const { days, studioIds } = scope;
  const [view, setView] = useState<View>('artists');

  const single = singleStudioScope(studioIds);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const attribution = useMemo(
    () => rebookAttribution(days, single),
    [days, single, bookings, artists, agents],
  );

  const artistRows = useMemo(
    () => attribution.staffRows.filter((r) => r.role === 'Artist').sort((a, b) => b.rebooks - a.rebooks),
    [attribution],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const agentRows = useMemo(
    () => agentPerformance(days, single),
    [days, single, agents, customers, deposits],
  );

  const rankedAgentRows = useMemo(
    () =>
      [...agentRows].sort((a, b) => {
        if (a.conversion === null && b.conversion === null) return b.deposits - a.deposits;
        if (a.conversion === null) return 1;
        if (b.conversion === null) return -1;
        if (b.conversion !== a.conversion) return b.conversion - a.conversion;
        return b.deposits - a.deposits;
      }),
    [agentRows],
  );

  // Manager view is network-wide by studio, unless studios are explicitly
  // selected - then filter to that selection (mirrors RebooksTab's "By store").
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const networkAttribution = useMemo(() => rebookAttribution(days), [days, bookings, artists, agents]);

  const managerRows = useMemo(() => {
    const selected = new Set(studioIds);
    return Object.entries(networkAttribution.byStudio)
      .filter(([sid]) => selected.size === 0 || selected.has(sid as StudioId))
      .map(([sid, counts]) => {
        const studio = studios[sid as StudioId];
        const manager = studio ? agents[studio.managerId] : undefined;
        const managerName = manager ? `${manager.firstName} ${manager.lastName}` : (studio?.managerId ?? '—');
        const total = counts.rebooked + counts.declined + counts.deferred;
        const rate = total > 0 ? counts.rebooked / total : 0;
        return {
          studioId: sid,
          studioName: studio?.name ?? sid,
          managerName,
          rate,
          deltaVsTarget: rate - REBOOK_MANAGER_TARGET,
        };
      })
      .sort((a, b) => b.rate - a.rate);
  }, [networkAttribution, studioIds, studios, agents]);

  return (
    <div className="space-y-6 max-w-6xl" data-testid="reporting-leaderboard">
      <section className="flex gap-2">
        {VIEWS.map((v) => (
          <button
            key={v.id}
            type="button"
            data-testid={`leaderboard-view-${v.id}`}
            onClick={() => setView(v.id)}
            aria-pressed={view === v.id}
            className={`px-3 py-1.5 rounded font-inter text-xs uppercase tracking-widest transition-colors ${
              view === v.id ? 'bg-gold-dim text-ink' : 'bg-ink-soft text-cream-soft hover:text-cream'
            }`}
          >
            {v.label}
          </button>
        ))}
      </section>

      {view === 'artists' ? (
        <section className="panel p-4" data-testid="leaderboard-artists">
          <h2 className="heading-serif text-lg text-cream mb-1">
            Artists, ranked by rebooks facilitated
            <InfoTip>In-chair rebooks each artist facilitated at checkout, in scope.</InfoTip>
          </h2>
          <div className="overflow-x-auto mt-2">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest text-cream-soft">
                  <th className="text-left px-2 py-2">#</th>
                  <th className="text-left px-2 py-2">Artist</th>
                  <th className="text-right px-2 py-2">Rebooks</th>
                </tr>
              </thead>
              <tbody>
                {artistRows.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="px-2 py-3 font-serif text-cream-soft text-sm">
                      No artist rebooks attributed yet.
                    </td>
                  </tr>
                ) : (
                  artistRows.map((r, i) => (
                    <tr key={r.id} className="border-t border-gold-dim/15">
                      <td className="px-2 py-2 font-mono text-cream-faint">{i + 1}</td>
                      <td className="px-2 py-2 font-inter text-cream whitespace-nowrap">{r.name}</td>
                      <td className="px-2 py-2 text-right font-mono text-gold-bright">{r.rebooks}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      {view === 'agents' ? (
        <section className="panel p-4" data-testid="leaderboard-agents">
          <h2 className="heading-serif text-lg text-cream mb-1">
            Agents, ranked by conversion
            <InfoTip>Deposits taken ÷ total calls handled (outbound + inbound), in scope. Ties broken by deposit count.</InfoTip>
          </h2>
          <div className="overflow-x-auto mt-2">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest text-cream-soft">
                  <th className="text-left px-2 py-2">#</th>
                  <th className="text-left px-2 py-2">Agent</th>
                  <th className="text-right px-2 py-2">Conversion</th>
                  <th className="text-right px-2 py-2">Deposits</th>
                </tr>
              </thead>
              <tbody>
                {rankedAgentRows.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-2 py-3 font-serif text-cream-soft text-sm">
                      No agents in scope.
                    </td>
                  </tr>
                ) : (
                  rankedAgentRows.map((r, i) => (
                    <tr key={r.agentId} className="border-t border-gold-dim/15">
                      <td className="px-2 py-2 font-mono text-cream-faint">{i + 1}</td>
                      <td className="px-2 py-2 font-inter text-cream whitespace-nowrap">{r.name}</td>
                      <td className="px-2 py-2 text-right font-mono text-gold-bright">{pct(r.conversion)}</td>
                      <td className="px-2 py-2 text-right font-mono text-cream-soft">{r.deposits.toLocaleString('en-AU')}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      {view === 'managers' ? (
        <section className="panel p-4" data-testid="leaderboard-managers">
          <h2 className="heading-serif text-lg text-cream mb-1">
            Managers, ranked by studio rebook rate
            <InfoTip>
              Rebooked ÷ (rebooked + declined + deferred) per studio, against the manager credit for that store, vs the 40%
              manager target.
            </InfoTip>
          </h2>
          <div className="overflow-x-auto mt-2">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest text-cream-soft">
                  <th className="text-left px-2 py-2">#</th>
                  <th className="text-left px-2 py-2">Studio</th>
                  <th className="text-left px-2 py-2">Manager</th>
                  <th className="text-right px-2 py-2">Rate</th>
                  <th className="text-right px-2 py-2">vs 40% target</th>
                </tr>
              </thead>
              <tbody>
                {managerRows.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-2 py-3 font-serif text-cream-soft text-sm">
                      No completed sessions with rebook outcomes yet.
                    </td>
                  </tr>
                ) : (
                  managerRows.map((r, i) => (
                    <tr key={r.studioId} className="border-t border-gold-dim/15">
                      <td className="px-2 py-2 font-mono text-cream-faint">{i + 1}</td>
                      <td className="px-2 py-2 font-inter text-cream whitespace-nowrap">{r.studioName}</td>
                      <td className="px-2 py-2 font-inter text-cream-soft whitespace-nowrap">{r.managerName}</td>
                      <td className="px-2 py-2 text-right font-mono text-cream">{pct(r.rate)}</td>
                      <td className={`px-2 py-2 text-right font-mono ${r.deltaVsTarget >= 0 ? 'text-mint' : 'text-blood'}`}>
                        {r.deltaVsTarget >= 0 ? '+' : ''}
                        {Math.round(r.deltaVsTarget * 100)}pt
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}
    </div>
  );
}
